import * as React from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { GenericErrorPage } from '@locus-taxy/encore-ui-toolkit';
import { telemetryService } from '@locus-taxy/encore-observability/telemetry';
import { getBugsnagClient } from '../utils/bugsnag';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  showRetry?: boolean;
  errorTitle?: string;
  errorMessage?: string;
  retryButtonText?: string;
}

type BugsnagFallbackProps = {
  error: Error;
  info: React.ErrorInfo;
  props: ErrorBoundaryProps;
};

interface ErrorBoundaryState {
  hasError: boolean;
}

class ReactErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    telemetryService.recordException(error.message, error, errorInfo);

    const bugsnagClient = getBugsnagClient();
    if (bugsnagClient) {
      bugsnagClient.notify(error, event => {
        event.addMetadata('react', {
          componentStack: errorInfo.componentStack,
        });
      });
    }
  }

  private readonly handleRetry = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      /* `!== undefined`, not truthiness: `fallback={null}` is a caller asking for
       * NOTHING to render, and a truthy check silently turned that into the
       * full-page GenericErrorPage — which is how the page-sized error banner
       * ended up inside an embedded detail section (DP-6140). */
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }

      const {
        showRetry = true,
        errorTitle,
        errorMessage,
        retryButtonText,
      } = this.props;

      return (
        <GenericErrorPage
          title={errorTitle}
          message={errorMessage}
          showRetry={showRetry}
          onRetry={this.handleRetry}
          retryButtonText={retryButtonText}
        />
      );
    }

    return this.props.children;
  }
}

function BugsnagFallback({
  error,
  info,
  props,
}: Readonly<BugsnagFallbackProps>) {
  React.useEffect(() => {
    telemetryService.recordException(error.message, error, info);
  }, [error, info]);

  /* Same explicit-null contract as the class boundary above. */
  if (props.fallback !== undefined) {
    return props.fallback;
  }

  const { showRetry = true, errorTitle, errorMessage, retryButtonText } = props;

  return (
    <GenericErrorPage
      title={errorTitle}
      message={errorMessage}
      showRetry={showRetry}
      onRetry={() => window.location.reload()}
      retryButtonText={retryButtonText}
    />
  );
}

export function ErrorBoundary(props: Readonly<ErrorBoundaryProps>) {
  const bugsnagClient = getBugsnagClient();
  const BugsnagErrorBoundary = React.useMemo(
    () => bugsnagClient?.getPlugin('react')?.createErrorBoundary(React),
    [bugsnagClient]
  );

  const FallbackComponent = React.useCallback(
    (fallbackProps: { error: Error; info: React.ErrorInfo }) => (
      <BugsnagFallback {...fallbackProps} props={props} />
    ),
    [props]
  );

  if (BugsnagErrorBoundary) {
    return (
      <BugsnagErrorBoundary FallbackComponent={FallbackComponent}>
        {props.children}
      </BugsnagErrorBoundary>
    );
  }

  return <ReactErrorBoundary {...props} />;
}
